import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Radar, Legend, BarChart, Bar, ReferenceLine, AreaChart, Area, LineChart, Line,
  LabelList,
} from "recharts";
import {
  fetchRunCompare, fetchRunEquityCurve, promoteBestForTicker, startTargetedPhase2,
  fetchConfigPromotionStats,
  PromoteNoPhase2Error,
  type RunCompareEntry, type RunTickerStats, type ConfigPromotionStats,
  type RunTickerPmStats, type RunEquityCurveResponse, type EquityCurvePoint,
} from "../api/client";
import { runStyle, runLabel } from "../lib/runStyle";

/** Side-by-side comparison of 2-15 TPE runs, framed as a hedge-fund-PM
 *  promotion view:
 *    - Risk/return scatter (the efficient-frontier shortlist)
 *    - Per-run "strategy report card" radar
 *    - Calmar heatmap (runs × tickers)
 *    - Alpha vs buy-and-hold bars
 *    - Config diff + raw distribution tables (legacy detail)
 *
 *  Every chart has a plain-English "How to read this" panel so first-time
 *  visitors aren't expected to bring trading-floor vocabulary with them.
 */

// Per-run styling (color, shape glyph, dash, pattern) lives in lib/runStyle.
// Color is treated as a REDUNDANT cue — the primary per-run identifier in
// each chart is a non-color channel (marker shape, line-dash, fill pattern,
// glyph in the legend). Hard project rule: see memory/user_colorblindness.md.
function colorFor(idx: number): string { return runStyle(idx).color; }

function fmtScore(v: number | null | undefined, digits = 3) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return Number(v).toFixed(digits);
}

// Compact human-readable duration. Used by the Phase 1 cost table.
function fmtDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds <= 0) {
    return "—";
  }
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.round(seconds % 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${s}s`;
}

function fmtVal(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function pickStat(per: Record<string, RunTickerStats>, ticker: string, metric: keyof RunTickerStats) {
  const s = per[ticker];
  if (!s) return null;
  return s[metric] as number | undefined;
}

// `Math.min(...arr)` / `Math.max(...arr)` spread the array as call args,
// which can overflow the JS engine's argument stack on the multi-thousand
// arrays this page used to feed it. Plain loops are O(n) and stack-safe.
function minOf(arr: number[]): number {
  let m = arr[0];
  for (let i = 1; i < arr.length; i++) if (arr[i] < m) m = arr[i];
  return m;
}
function maxOf(arr: number[]): number {
  let m = arr[0];
  for (let i = 1; i < arr.length; i++) if (arr[i] > m) m = arr[i];
  return m;
}

// Tiny seedable PRNG (mulberry32). Deterministic per seed, so a series
// downsample doesn't flicker across renders. ~7 LOC; not worth pulling
// in a dep.
function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Reservoir-style downsample. Returns the array unchanged when it's
// already <= n; otherwise picks n elements uniformly at random with a
// fixed seed for stable cross-render output.
function downsample<T>(arr: T[], n: number, seed = 1): T[] {
  if (arr.length <= n) return arr;
  const rand = mulberry32(seed);
  const out = arr.slice(0, n);
  for (let i = n; i < arr.length; i++) {
    const j = Math.floor(rand() * (i + 1));
    if (j < n) out[j] = arr[i];
  }
  return out;
}

function pmMedian(pm: RunTickerPmStats | undefined, field: keyof RunTickerPmStats["metrics"]): number | null {
  const m = pm?.metrics[field];
  if (!m || m === null || typeof m.median !== "number") return null;
  return m.median;
}

/** Educational "How to read this" panel. Wraps every chart so a
 *  first-time visitor can self-teach. Bullet-style, plain English. */
function Explainer({ title, lines }: { title: string; lines: string[] }) {
  return (
    <div style={{
      background: "#0b1220",
      border: "1px solid #1e3a8a",
      borderLeft: "3px solid #3b82f6",
      borderRadius: 4,
      padding: "10px 12px",
      marginBottom: 12,
      fontSize: 12,
      lineHeight: 1.55,
      color: "#cbd5e1",
    }}>
      <div style={{ color: "#93c5fd", fontWeight: 600, marginBottom: 4, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
        📖 {title}
      </div>
      {lines.map((line, i) => (
        <div key={i} style={{ marginTop: i === 0 ? 0 : 4 }}>{line}</div>
      ))}
    </div>
  );
}

function ColorScale({ value, min, max }: { value: number; min: number; max: number }) {
  if (min === max) return <>{fmtScore(value)}</>;
  const norm = (value - min) / (max - min);
  let color = "#cbd5e1";
  if (norm > 0.66) color = "#22c55e";
  else if (norm < 0.33) color = "#ef4444";
  return <span style={{ color, fontWeight: 600 }}>{fmtScore(value)}</span>;
}

/** Heatmap cell — replaces the old red→slate→green diverging gradient,
 *  which was unreadable for the protan-CVD primary user (subtle red/green
 *  shades all look like dark brown to him). Instead we double-encode the
 *  magnitude with TWO non-color channels:
 *    1. A horizontal in-cell bar (length = |value| / scale, direction =
 *       sign). Length encodes magnitude redundantly with the number.
 *    2. A per-row rank superscript (1st / 2nd / 3rd / … nth) so the row
 *       winner is identifiable purely from text even with no color.
 *  Color is used only as a tertiary cue: BLUE for positive, ORANGE for
 *  negative — the one hue pairing the user reads reliably. */
function HeatCell({
  value, gMin, gMax, rank, runCount, digits = 3, suffix = "",
}: {
  value: number | null;
  gMin: number;
  gMax: number;
  /** 1-indexed rank of this cell among the same row (1 = best in row). */
  rank?: number | null;
  /** Total run count in the row, for rank display ("1/4"). */
  runCount?: number;
  digits?: number;
  suffix?: string;
}) {
  if (value === null || Number.isNaN(value)) {
    return <td className="num" style={{ color: "#475569" }}>—</td>;
  }
  // Magnitude bar length: normalize against the larger of |gMin|, gMax so
  // positive and negative bars share a common scale.
  const scale = Math.max(Math.abs(gMin), Math.abs(gMax), 1e-9);
  const pct = Math.max(0, Math.min(1, Math.abs(value) / scale));
  const positive = value >= 0;
  const barColor = positive ? "#56B4E9" : "#E69F00";  // blue / orange anchor
  const rankBadge = rank && runCount
    ? `${rank}/${runCount}`
    : null;
  // Rank text color: bright white for #1, dimmer for the rest. Single-hue
  // luminance ramp (not red↔green) so it stays readable.
  const rankColor = rank === 1 ? "#FFFFFF" : "#94A3B8";
  return (
    <td
      className="num"
      style={{
        background: "#0f172a",
        color: "#e2e8f0",
        fontWeight: 600,
        fontVariantNumeric: "tabular-nums",
        padding: "4px 8px",
        minWidth: 92,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
        <span>{value.toFixed(digits)}{suffix}</span>
        {rankBadge && (
          <span style={{ fontSize: 9, color: rankColor, fontWeight: 700, letterSpacing: 0.3 }}>
            {rank === 1 ? "★" : ""}{rankBadge}
          </span>
        )}
      </div>
      <div style={{
        marginTop: 3,
        height: 4,
        background: "#1e293b",
        borderRadius: 1,
        position: "relative",
      }}>
        <div style={{
          position: "absolute",
          left: positive ? "50%" : `${50 - pct * 50}%`,
          width: `${pct * 50}%`,
          height: "100%",
          background: barColor,
          borderRadius: 1,
        }} />
        {/* Center tick so polarity is visually unambiguous. */}
        <div style={{
          position: "absolute",
          left: "50%",
          top: -1,
          width: 1,
          height: 6,
          background: "#64748b",
        }} />
      </div>
    </td>
  );
}

/** Renders a small SVG glyph matching the per-run shape. Used in the
 *  top legend card and in any inline run-mention chip so the SAME shape
 *  the chart uses as the marker shows up next to the config name. */
function RunGlyph({ idx, size = 11 }: { idx: number; size?: number }) {
  const { shape, color } = runStyle(idx);
  const c = size / 2;
  const r = (size / 2) - 1;
  switch (shape) {
    case "square":
      return <svg width={size} height={size} style={{ verticalAlign: "middle" }}><rect x={1} y={1} width={size - 2} height={size - 2} fill={color} /></svg>;
    case "triangle":
      return <svg width={size} height={size} style={{ verticalAlign: "middle" }}><polygon points={`${c},1 ${size - 1},${size - 1} 1,${size - 1}`} fill={color} /></svg>;
    case "diamond":
      return <svg width={size} height={size} style={{ verticalAlign: "middle" }}><polygon points={`${c},1 ${size - 1},${c} ${c},${size - 1} 1,${c}`} fill={color} /></svg>;
    case "star": {
      // 5-point star
      const points: string[] = [];
      for (let i = 0; i < 10; i++) {
        const ang = -Math.PI / 2 + (i * Math.PI) / 5;
        const rr = i % 2 === 0 ? r : r / 2.2;
        points.push(`${c + rr * Math.cos(ang)},${c + rr * Math.sin(ang)}`);
      }
      return <svg width={size} height={size} style={{ verticalAlign: "middle" }}><polygon points={points.join(" ")} fill={color} /></svg>;
    }
    case "cross":
      return <svg width={size} height={size} style={{ verticalAlign: "middle" }}>
        <rect x={c - 1} y={1} width={2} height={size - 2} fill={color} />
        <rect x={1} y={c - 1} width={size - 2} height={2} fill={color} />
      </svg>;
    case "wye":
      return <svg width={size} height={size} style={{ verticalAlign: "middle" }}>
        <line x1={c} y1={c} x2={c} y2={1} stroke={color} strokeWidth={2} />
        <line x1={c} y1={c} x2={1} y2={size - 1} stroke={color} strokeWidth={2} />
        <line x1={c} y1={c} x2={size - 1} y2={size - 1} stroke={color} strokeWidth={2} />
      </svg>;
    default:
      return <svg width={size} height={size} style={{ verticalAlign: "middle" }}><circle cx={c} cy={c} r={r} fill={color} /></svg>;
  }
}

/** Mini SVG showing the dash pattern as a short colored line. Used in the
 *  top legend card so users can match a line in the rolling-Sharpe chart
 *  back to a specific run by its dash signature, not its color. */
function DashSwatch({ idx, width = 26, height = 4 }: { idx: number; width?: number; height?: number }) {
  const { color, dash } = runStyle(idx);
  return (
    <svg width={width} height={height} style={{ verticalAlign: "middle" }}>
      <line
        x1={0} y1={height / 2} x2={width} y2={height / 2}
        stroke={color}
        strokeWidth={2}
        strokeDasharray={dash}
      />
    </svg>
  );
}

/** SVG <defs> with one fill pattern per run. Mounted INSIDE each
 *  Recharts container that wants pattern-fills (Recharts inlines its
 *  children into the chart's <svg>, which is where SVG defs need to be).
 *  Patterns are stroked in the run's color so even on dark bg they read
 *  as that run's signature. The bg is filled with a low-luminance dark
 *  slate so the chart's gridlines don't bleed through.
 *
 *  Use as: <Bar fill={`url(#run-fill-${idx})`}>. The pattern id is
 *  parameterized by both the run index (cycles through pattern types)
 *  and the run's color so two bars from different runs don't collide. */
function RunFillPatternDefs({ runCount }: { runCount: number }) {
  return (
    <defs>
      {Array.from({ length: runCount }, (_, idx) => {
        const { color, pattern } = runStyle(idx);
        const id = `run-fill-${idx}`;
        switch (pattern) {
          case "diag":
            return (
              <pattern key={idx} id={id} patternUnits="userSpaceOnUse" width={8} height={8}>
                <rect width={8} height={8} fill={color} fillOpacity={0.35} />
                <path d="M0,8 L8,0" stroke={color} strokeWidth={2} />
              </pattern>
            );
          case "cross":
            return (
              <pattern key={idx} id={id} patternUnits="userSpaceOnUse" width={8} height={8}>
                <rect width={8} height={8} fill={color} fillOpacity={0.25} />
                <path d="M0,8 L8,0 M0,0 L8,8" stroke={color} strokeWidth={1.5} />
              </pattern>
            );
          case "dots":
            return (
              <pattern key={idx} id={id} patternUnits="userSpaceOnUse" width={6} height={6}>
                <rect width={6} height={6} fill={color} fillOpacity={0.25} />
                <circle cx={3} cy={3} r={1.4} fill={color} />
              </pattern>
            );
          case "vstripe":
            return (
              <pattern key={idx} id={id} patternUnits="userSpaceOnUse" width={6} height={8}>
                <rect width={6} height={8} fill={color} fillOpacity={0.3} />
                <rect x={0} width={2.5} height={8} fill={color} />
              </pattern>
            );
          default:  // "solid"
            return (
              <pattern key={idx} id={id} patternUnits="userSpaceOnUse" width={1} height={1}>
                <rect width={1} height={1} fill={color} />
              </pattern>
            );
        }
      })}
    </defs>
  );
}

/** Custom Radar dot — renders the per-run shape (not just a colored
 *  circle), so the radar's vertices are identifiable by shape alone. */
function RadarVertexDot(idx: number) {
  const { color, shape } = runStyle(idx);
  return function CustomDot(props: { cx?: number; cy?: number; key?: string | number }) {
    const cx = props.cx ?? 0;
    const cy = props.cy ?? 0;
    const r = 4;
    switch (shape) {
      case "square":
        return <rect x={cx - r} y={cy - r} width={r * 2} height={r * 2} fill={color} stroke={color} />;
      case "triangle":
        return <polygon points={`${cx},${cy - r - 1} ${cx + r},${cy + r - 1} ${cx - r},${cy + r - 1}`} fill={color} stroke={color} />;
      case "diamond":
        return <polygon points={`${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`} fill={color} stroke={color} />;
      case "star": {
        const points: string[] = [];
        for (let i = 0; i < 10; i++) {
          const ang = -Math.PI / 2 + (i * Math.PI) / 5;
          const rr = i % 2 === 0 ? r + 1 : (r + 1) / 2.2;
          points.push(`${cx + rr * Math.cos(ang)},${cy + rr * Math.sin(ang)}`);
        }
        return <polygon points={points.join(" ")} fill={color} stroke={color} />;
      }
      case "cross":
        return (
          <g>
            <rect x={cx - 1} y={cy - r} width={2} height={r * 2} fill={color} />
            <rect x={cx - r} y={cy - 1} width={r * 2} height={2} fill={color} />
          </g>
        );
      case "wye":
        return (
          <g stroke={color} strokeWidth={2}>
            <line x1={cx} y1={cy} x2={cx} y2={cy - r} />
            <line x1={cx} y1={cy} x2={cx - r} y2={cy + r - 1} />
            <line x1={cx} y1={cy} x2={cx + r} y2={cy + r - 1} />
          </g>
        );
      default:
        return <circle cx={cx} cy={cy} r={r} fill={color} stroke={color} />;
    }
  };
}

// Memo'd promote-cell. Was inline in the median table; rebuilding all
// rows on every setPromoteCells() update meant the whole page (charts
// included) chain-re-rendered. Now only the clicked cell's row updates.
const PromoteMedianCell = memo(function PromoteMedianCell({
  runId, ticker, median, lo, hi, status, onPromote,
}: {
  runId: string;
  ticker: string;
  median: number;
  lo: number;
  hi: number;
  status: string | undefined;
  onPromote: (runId: string, ticker: string, median: number) => void;
}) {
  const busy = status === "busy";
  return (
    <td className="num">
      <span
        role="link"
        tabIndex={0}
        onClick={() => !busy && onPromote(runId, ticker, median)}
        onKeyDown={(e) => {
          if (busy) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onPromote(runId, ticker, median);
          }
        }}
        title={busy
          ? "Promoting…"
          : status && !status.startsWith("✓")
            ? status
            : `Click to promote best Phase 2 trial for ${ticker} from ${runId}`}
        style={{
          cursor: busy ? "wait" : "pointer",
          textDecoration: "underline",
          textDecorationStyle: "dotted",
          textUnderlineOffset: 3,
          textDecorationColor: "#475569",
          opacity: busy ? 0.6 : 1,
        }}
      >
        <ColorScale value={median} min={lo} max={hi} />
      </span>
      {status && status !== "busy" && (
        <div style={{
          fontSize: 10,
          color: status.startsWith("✓") ? "#86efac" : "#fca5a5",
          marginTop: 2,
          fontWeight: 600,
        }}>{status}</div>
      )}
    </td>
  );
});

// ----------------- Radar data prep -----------------

/** Strategy report card. Each axis is a single dimension a hedge-fund PM
 *  cares about when deciding whether to allocate capital. */
const RADAR_AXES: Array<{
  key: string;
  label: string;
  /** Per-axis tooltip explaining what the axis means in plain English. */
  tip: string;
}> = [
  { key: "return", label: "Return",      tip: "Median CAGR across this run's trials. Higher = the model made more money on the test window. Reference: SPY long-run ≈ 10%/yr, top hedge funds ≈ 15-20%/yr." },
  { key: "risk",   label: "Risk control", tip: "Inverse of median |max drawdown|. Higher on the radar = the model held losses to a small fraction of equity. Drawdown is the worst peak-to-trough loss; PMs scrutinize this BEFORE return." },
  { key: "win",    label: "Win rate",    tip: "Median % of closed trades that ended profitable. Higher = more frequent winners. 50% is coin-flip; consistently >55% is a meaningful edge." },
  { key: "edge",   label: "Profit factor", tip: "Median gross profit ÷ gross loss. 1.0 = breakeven on edge alone; >1.5 means winners decisively outpace losers in dollars (different from win rate, which only counts trade count)." },
  { key: "alpha",  label: "Alpha vs B&H", tip: "Median (CAGR − buy-and-hold CAGR). Did the model add value vs just sitting in the ticker? Positive = the model is doing real work. Negative = a buy-and-hold of the same ticker would have beaten it." },
];

/** Per-axis transformation: standardize "higher = better, in [0,1]" so all
 *  axes can sit on one polar chart. Normalization is RELATIVE TO THE
 *  RUNS BEING COMPARED — a run plotted at 1.0 on Return is the best of
 *  the compared runs, not a universal max. Document this on the chart. */
function buildRadarData(runs: RunCompareEntry[]): Array<Record<string, number | string>> {
  const perRun: Record<string, Record<string, number | null>> = {};
  for (const r of runs) {
    const cagrs: number[] = [];
    const mdds: number[] = [];
    const wins: number[] = [];
    const pfs: number[] = [];
    const alphas: number[] = [];
    for (const t of Object.keys(r.per_ticker_pm ?? {})) {
      const pm = r.per_ticker_pm?.[t];
      const cagr = pmMedian(pm, "cagr_pct");
      const mdd = pmMedian(pm, "max_drawdown_pct");
      const win = pmMedian(pm, "win_rate_trade");
      const pf = pmMedian(pm, "profit_factor");
      const bh = pmMedian(pm, "buy_and_hold_return_pct");
      if (cagr !== null) cagrs.push(cagr);
      if (mdd !== null) mdds.push(Math.abs(mdd));
      if (win !== null) wins.push(win);
      if (pf !== null) pfs.push(pf);
      if (cagr !== null && bh !== null) alphas.push(cagr - bh);
    }
    const med = (arr: number[]): number | null => {
      if (arr.length === 0) return null;
      const s = [...arr].sort((a, b) => a - b);
      const mid = Math.floor(s.length / 2);
      return s.length % 2 === 0 ? (s[mid - 1] + s[mid]) / 2 : s[mid];
    };
    perRun[r.run_id] = {
      return: med(cagrs),
      risk:   med(mdds),  // raw |MDD|, inverted below
      win:    med(wins),
      edge:   med(pfs),
      alpha:  med(alphas),
    };
  }

  // Global ranges across runs for normalization.
  const ranges: Record<string, { min: number; max: number }> = {};
  for (const axis of ["return", "risk", "win", "edge", "alpha"]) {
    const vs: number[] = [];
    for (const rid of Object.keys(perRun)) {
      const v = perRun[rid][axis];
      if (v !== null && v !== undefined) vs.push(v);
    }
    ranges[axis] = vs.length ? { min: minOf(vs), max: maxOf(vs) } : { min: 0, max: 1 };
  }

  // Normalize: higher = better, in [0.05, 1.0].
  // The 0.05 floor keeps the polygon visible even for the worst run.
  const normalize = (axis: string, raw: number | null): number => {
    if (raw === null) return 0.05;
    const { min, max } = ranges[axis];
    if (max === min) return 0.5;
    let frac = (raw - min) / (max - min);
    // "risk" is |MDD|: small is good → invert.
    if (axis === "risk") frac = 1 - frac;
    return 0.05 + 0.95 * Math.max(0, Math.min(1, frac));
  };

  // Recharts radar expects one record per axis, with one key per series.
  const out: Array<Record<string, number | string>> = RADAR_AXES.map((ax) => {
    const row: Record<string, number | string> = { axis: ax.label };
    for (const r of runs) {
      const raw = perRun[r.run_id]?.[ax.key] ?? null;
      row[r.run_id] = normalize(ax.key, raw);
      // Stash the raw value too for tooltips. Recharts ignores unknown keys.
      row[`${r.run_id}__raw`] = raw === null ? "n/a" : raw.toFixed(3);
    }
    return row;
  });
  return out;
}

// ----------------- Component -----------------

// ----------------- Equity-curve aggregation helpers -----------------

/** Daily returns from a downsampled equity curve. Index 0 has no return. */
function dailyReturns(points: EquityCurvePoint[]): number[] {
  if (points.length < 2) return [];
  const out: number[] = [];
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1].equity;
    out.push(prev > 0 ? points[i].equity / prev - 1 : 0);
  }
  return out;
}

/** Pearson correlation. NaN-safe; returns 0 for zero-length / zero-variance
 *  vectors (rather than NaN) so the correlation cell can still render. */
function pearson(a: number[], b: number[]): number {
  const n = Math.min(a.length, b.length);
  if (n < 2) return 0;
  let sa = 0, sb = 0;
  for (let i = 0; i < n; i++) { sa += a[i]; sb += b[i]; }
  const ma = sa / n, mb = sb / n;
  let num = 0, da = 0, db = 0;
  for (let i = 0; i < n; i++) {
    const xa = a[i] - ma;
    const xb = b[i] - mb;
    num += xa * xb;
    da += xa * xa;
    db += xb * xb;
  }
  const denom = Math.sqrt(da * db);
  return denom > 1e-12 ? num / denom : 0;
}

/** Align two daily-return series by date (truncate to overlapping dates).
 *  Both equity curves nominally share the same test window, but if a
 *  trial truncated early or a date is missing on one side, intersection
 *  keeps the math clean. */
function alignedReturns(
  pa: EquityCurvePoint[], pb: EquityCurvePoint[],
): { a: number[]; b: number[] } {
  const ma = new Map<string, number>();
  const mb = new Map<string, number>();
  const ra = dailyReturns(pa);
  const rb = dailyReturns(pb);
  for (let i = 1; i < pa.length; i++) ma.set(pa[i].date, ra[i - 1]);
  for (let i = 1; i < pb.length; i++) mb.set(pb[i].date, rb[i - 1]);
  const dates = [...ma.keys()].filter((d) => mb.has(d)).sort();
  const a: number[] = []; const b: number[] = [];
  for (const d of dates) { a.push(ma.get(d)!); b.push(mb.get(d)!); }
  return { a, b };
}

// Correlation cell: green = uncorrelated (good for ensembling), red =
// highly correlated (redundant in an ensemble — they make the same bets).
function CorrCell({ r }: { r: number }) {
  const abs = Math.abs(r);
  // Polarity: closer to 0 = greener. Strong positive correlation = red.
  // Negative correlation (rare for two trading strategies on the same
  // ticker) is treated as "informative" (yellow), since two anti-correlated
  // strategies hedge each other.
  let bg = "#1e293b";
  let fg = "#e2e8f0";
  if (r > 0) {
    // Positive corr — fade slate → red as |r| → 1.
    const t = abs;
    const rr = Math.round(30 + (239 - 30) * t);
    const gg = Math.round(41 + (68 - 41) * t);
    const bb = Math.round(59 + (68 - 59) * t);
    bg = `rgb(${rr}, ${gg}, ${bb})`;
    fg = t > 0.55 ? "#0f172a" : "#e2e8f0";
  } else if (r < 0) {
    bg = "#7c2d12";  // amber-900 — uncommon, draws attention
    fg = "#fed7aa";
  }
  return (
    <td className="num" style={{ background: bg, color: fg, fontWeight: 600 }}>
      {r.toFixed(2)}
    </td>
  );
}

// ----------------- Component -----------------

export function RunComparePage() {
  const [search] = useSearchParams();
  const ids = (search.get("ids") || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const phaseRaw = search.get("phase");
  const phase = phaseRaw ? Number(phaseRaw) : 1;

  const { data, isLoading, error } = useQuery({
    queryKey: ["run-compare", ids.join(","), phase],
    queryFn: () => fetchRunCompare(ids, phase),
    enabled: ids.length >= 2,
    staleTime: 60_000,
  });

  // Per-config promote-time rejection history. Used to flag a run in the
  // legend if its config has a track record of failing the consistency
  // gate — answers "if I promote this config now, will it fall over
  // like last time?". Aggregated across all runs/tickers; per-ticker
  // breakdown is a future enhancement.
  const { data: promotionStats } = useQuery({
    queryKey: ["config-promotion-stats"],
    queryFn: fetchConfigPromotionStats,
    staleTime: 5 * 60_000,
  });
  const statsByConfig = useMemo(() => {
    const out: Record<string, ConfigPromotionStats> = {};
    for (const s of promotionStats?.configs ?? []) out[s.config_name] = s;
    return out;
  }, [promotionStats]);

  // Ticker selected for the per-trial drilldown panel (underwater,
  // rolling Sharpe, correlation matrix). Defaults to the first ticker
  // present in the compare response; updated by the dropdown.
  const [drilldownTicker, setDrilldownTicker] = useState<string>("");
  // Drilldown section is opt-in: with N runs it mounts N equity-curve
  // fetches and N+1 Recharts charts (N underwater + 1 Sharpe overlay),
  // each with its own ResponsiveContainer. For 15-run compares that's
  // enough SVG churn to peg a renderer process. Gate behind a button so
  // the user only pays the cost when they actually want the view.
  const [drilldownEnabled, setDrilldownEnabled] = useState(false);

  // Per-cell promote status. Keyed by "run_id|ticker" so multiple cells
  // can show their own ok/err message at once.
  const [promoteCells, setPromoteCells] = useState<Record<string, "busy" | "ok" | string>>({});
  // Promote confirm modal. Click a median cell → opens this with the
  // (run_id, ticker, median) payload; clicking Confirm fires the API.
  // Custom modal instead of window.confirm because iOS Safari will
  // silently suppress repeated native dialogs from the same origin.
  const [promoteTarget, setPromoteTarget] = useState<null | {
    runId: string; ticker: string; median: number;
  }>(null);
  // Stable across renders so the memo'd PromoteMedianCell can bail out
  // of re-render when nothing else about the cell changed. Busy-state
  // guard lives on the cell's onClick (it has `busy` already in props).
  const handlePromoteCell = useCallback((runId: string, ticker: string, median: number) => {
    setPromoteTarget({ runId, ticker, median });
  }, []);
  // Tracks targets that came back with "no Phase 2 yet" — keeps the
  // modal open showing the "Start Phase 2 for {ticker}" follow-up CTA.
  const [noPhase2Target, setNoPhase2Target] = useState<null | { runId: string; ticker: string; median: number }>(null);

  async function runPromote() {
    if (!promoteTarget) return;
    const { runId, ticker } = promoteTarget;
    const key = `${runId}|${ticker}`;
    setPromoteTarget(null);
    setPromoteCells((s) => ({ ...s, [key]: "busy" }));
    try {
      const res = await promoteBestForTicker({ run_id: runId, ticker });
      const ck = res.resolved?.config_key?.slice(0, 8) ?? "?";
      const rep = res.resolved?.replicate ?? "?";
      setPromoteCells((s) => ({ ...s, [key]: `✓ ${ck}/r${rep}` }));
    } catch (e) {
      if (e instanceof PromoteNoPhase2Error) {
        // Switch the cell status to a neutral hint and reopen the modal
        // in "no Phase 2" mode so the user can decide to start one.
        setPromoteCells((s) => ({ ...s, [key]: "" }));
        setNoPhase2Target({ runId, ticker, median: 0 });
        return;
      }
      const msg = e instanceof Error ? e.message : String(e);
      setPromoteCells((s) => ({ ...s, [key]: `✗ ${msg.slice(0, 80)}` }));
    }
  }

  async function runStartTargetedPhase2() {
    if (!noPhase2Target) return;
    const { runId, ticker } = noPhase2Target;
    const key = `${runId}|${ticker}`;
    setNoPhase2Target(null);
    setPromoteCells((s) => ({ ...s, [key]: "busy" }));
    try {
      const res = await startTargetedPhase2({ run_id: runId, ticker });
      const label = res.queued
        ? `⏳ Phase 2 queued (${res.in_flight}/${res.cap} runs in flight)`
        : `↻ Phase 2 started (${res.execution_arn?.split(":").pop()?.slice(0, 18) ?? "running"})`;
      setPromoteCells((s) => ({ ...s, [key]: label }));
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setPromoteCells((s) => ({ ...s, [key]: `✗ ${msg.slice(0, 80)}` }));
    }
  }

  // Union of tickers across all runs (some runs may include different tickers).
  const tickers = useMemo(() => {
    if (!data) return [] as string[];
    const all = new Set<string>();
    for (const r of data.runs) {
      Object.keys(r.per_ticker).forEach((t) => all.add(t));
    }
    return [...all].sort();
  }, [data]);

  // Radar data + scatter points + alpha-bar data are all derived from the
  // /runs/compare response. Memoized so panning around React doesn't
  // recompute on every render.
  const radarData = useMemo(() => (data ? buildRadarData(data.runs) : []), [data]);

  const scatterSeries = useMemo(() => {
    type Series = {
      run_id: string;
      label: string;
      color: string;
      shape: ReturnType<typeof runStyle>["shape"];
      points: Array<{ x: number; y: number; z: number; ticker: string }>;
      sampled: number;
      total: number;
    };
    if (!data) return [] as Series[];
    // Belt-and-suspenders cap on TOTAL dots per run-series. The server
    // caps per (run, ticker); a run with many tickers can still flood
    // the SVG (e.g. 20 tickers × 100 cap = 2k dots per series × 15
    // runs = 30k SVG circles). SCATTER_PER_RUN_CAP keeps the rendered
    // dot count bounded regardless of ticker breadth.
    const SCATTER_PER_RUN_CAP = 150;
    return data.runs.map<Series>((r, idx) => {
      const points: Array<{ x: number; y: number; z: number; ticker: string }> = [];
      let total = 0;
      for (const t of Object.keys(r.per_ticker_pm ?? {})) {
        const pm = r.per_ticker_pm?.[t];
        if (!pm) continue;
        // trial_points_total is the pre-cap count; fall back to the
        // returned length when an older backend omits the field.
        total += pm.trial_points_total ?? pm.trial_points.length;
        for (const p of pm.trial_points) {
          points.push({
            // x = |MDD|, magnitude of drawdown (positive for chart)
            x: Math.abs(p.max_drawdown_pct),
            y: p.cagr_pct,
            // z = num_trades, scaled for ZAxis. Capped so a few outliers
            // don't make every other dot invisible.
            z: Math.max(8, Math.min(80, p.num_trades / 5)),
            ticker: t,
          });
        }
      }
      // Seed by index so each run series gets a stable but distinct sample.
      const sampled = downsample(points, SCATTER_PER_RUN_CAP, idx + 1);
      const st = runStyle(idx);
      return {
        run_id: r.run_id,
        label: runLabel(r),
        color: st.color,
        shape: st.shape,
        points: sampled,
        sampled: sampled.length,
        total,
      };
    });
  }, [data]);

  // Total dots actually rendered vs total trials behind them. When the
  // server-side cap kicked in, the scatter shows a sampled subset — the
  // footnote tells the user that's what they're looking at.
  const scatterCounts = useMemo(() => {
    let sampled = 0;
    let total = 0;
    for (const s of scatterSeries) { sampled += s.sampled; total += s.total; }
    return { sampled, total, truncated: total > sampled };
  }, [scatterSeries]);

  // Heatmap value ranges across the whole grid (so cell colors are
  // comparable across cells, not just within a row).
  const heatRange = useMemo(() => {
    if (!data) return { min: 0, max: 0 };
    const vs: number[] = [];
    for (const r of data.runs) {
      for (const t of tickers) {
        const m = pickStat(r.per_ticker, t, "median");
        if (typeof m === "number") vs.push(m);
      }
    }
    if (!vs.length) return { min: 0, max: 0 };
    return { min: minOf(vs), max: maxOf(vs) };
  }, [data, tickers]);

  // Alpha-vs-B&H bar data: one bar group per ticker, one bar per run,
  // value = (median CAGR for that run on that ticker) - (median B&H).
  const alphaBarData = useMemo(() => {
    if (!data) return [] as Array<Record<string, number | string>>;
    return tickers.map((t) => {
      const row: Record<string, number | string> = { ticker: t };
      for (const r of data.runs) {
        const pm = r.per_ticker_pm?.[t];
        const cagr = pmMedian(pm, "cagr_pct");
        const bh = pmMedian(pm, "buy_and_hold_return_pct");
        if (cagr !== null && bh !== null) row[r.run_id] = +(cagr - bh).toFixed(3);
      }
      return row;
    });
  }, [data, tickers]);

  // IS-vs-OOS bar data: one row per run × (CAGR, MDD). Aggregates the
  // PM medians of is_* vs the OOS counterparts across all tickers in the
  // run (median-of-medians for robustness). Only Phase 2 trials carry
  // is_* fields, so this is empty for Phase 1-only views.
  const isOosCagrBarData = useMemo(() => {
    if (!data) return [] as Array<Record<string, number | string>>;
    return data.runs.map((r) => {
      const isVals: number[] = [];
      const oosVals: number[] = [];
      for (const t of Object.keys(r.per_ticker_pm ?? {})) {
        const pm = r.per_ticker_pm?.[t];
        const isMed = pmMedian(pm, "is_cagr_pct");
        const oosMed = pmMedian(pm, "cagr_pct");
        if (isMed !== null) isVals.push(isMed);
        if (oosMed !== null) oosVals.push(oosMed);
      }
      const med = (arr: number[]): number | null => {
        if (!arr.length) return null;
        const s = [...arr].sort((a, b) => a - b);
        const m = Math.floor(s.length / 2);
        return s.length % 2 === 0 ? (s[m - 1] + s[m]) / 2 : s[m];
      };
      const isM = med(isVals);
      const oosM = med(oosVals);
      return {
        run_id: r.run_id,
        "In-sample (training)": isM === null ? 0 : +isM.toFixed(3),
        "Out-of-sample (test)": oosM === null ? 0 : +oosM.toFixed(3),
        _isAvailable: isM !== null,
      };
    });
  }, [data]);

  const hasAnyIsData = useMemo(
    () => isOosCagrBarData.some((r) => r._isAvailable),
    [isOosCagrBarData],
  );

  // Default the drilldown ticker once the compare response arrives.
  useEffect(() => {
    if (!drilldownTicker && tickers.length > 0) {
      setDrilldownTicker(tickers[0]);
    }
  }, [tickers, drilldownTicker]);

  // Parallel per-run fetches of the BEST trial's equity curve for the
  // currently selected ticker. React Query handles caching + dedupe per
  // (runId, ticker) so flipping tickers back and forth is instant.
  const equityQueries = useQueries({
    queries: ids.map((rid) => ({
      queryKey: ["run-equity", rid, drilldownTicker],
      queryFn: () => fetchRunEquityCurve(rid, drilldownTicker),
      enabled: drilldownEnabled && !!drilldownTicker,
      staleTime: 5 * 60_000,
      retry: false,  // 404 (no artifact) shouldn't auto-retry
    })),
  });

  // Pre-shape per-run equity series for the underwater / rolling-Sharpe
  // small multiples. `null` slot for runs whose fetch errored or returned
  // no points (pre-deploy trials).
  //
  // Why the signature dep instead of `equityQueries`: useQueries returns
  // a new wrapper array on every render, so depending on it directly
  // would bust every downstream memo each render. The per-query
  // dataUpdatedAt / errorUpdatedAt / isLoading triple flips only when
  // React Query actually has new state, which is what we want to react to.
  type SeriesEntry = {
    run_id: string;
    label: string;
    color: string;
    dash: string;
    resp: RunEquityCurveResponse | null;
    error: string | null;
    loading: boolean;
  };
  const idsKey = ids.join(",");
  const equitySig = equityQueries.map((q) => `${q.dataUpdatedAt}|${q.errorUpdatedAt}|${q.isLoading ? 1 : 0}`).join("/");
  // Map run_id → its label looked up from the compare response (cheap
  // index lookup, but only valid when data has loaded; fall back to
  // run_id itself so the chart still renders).
  const labelByRunId: Record<string, string> = useMemo(() => {
    const out: Record<string, string> = {};
    (data?.runs ?? []).forEach((r) => { out[r.run_id] = runLabel(r); });
    return out;
  }, [data]);
  const equitySeries: SeriesEntry[] = useMemo(() => {
    return ids.map((rid, idx) => {
      const q = equityQueries[idx];
      const st = runStyle(idx);
      return {
        run_id: rid,
        label: labelByRunId[rid] || rid,
        color: st.color,
        dash: st.dash,
        resp: (q.data as RunEquityCurveResponse | undefined) ?? null,
        error: q.error ? String((q.error as Error).message || q.error) : null,
        loading: q.isLoading,
      };
    });
    // ids + equitySig + labelByRunId fully describe the inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, equitySig, labelByRunId]);

  // Pairwise correlation of OOS daily returns between runs (on the
  // selected ticker's best trial). N×N table; diagonal is 1.0.
  const corrMatrix = useMemo(() => {
    return equitySeries.map((_, i) =>
      equitySeries.map((_unused, j) => {
        if (i === j) return 1;
        const pi = equitySeries[i].resp?.oos.points ?? [];
        const pj = equitySeries[j].resp?.oos.points ?? [];
        if (!pi.length || !pj.length) return NaN;
        const { a, b } = alignedReturns(pi, pj);
        return pearson(a, b);
      }),
    );
  }, [equitySeries]);

  // Rolling-Sharpe overlay: one row per date, one column per run. The
  // previous inline IIFE rebuilt this on every render AND did O(dates ×
  // runs × points) work via `.find()`. Per-run Map lookup drops it to
  // O(dates × runs).
  const sharpeChartData = useMemo<Array<Record<string, string | number | null>> | null>(() => {
    const dateSet = new Set<string>();
    equitySeries.forEach((s) => (s.resp?.oos.points ?? []).forEach((p) => dateSet.add(p.date)));
    const dates = [...dateSet].sort();
    if (dates.length === 0) return null;
    const lookups = equitySeries.map((s) => {
      const m = new Map<string, number | null>();
      (s.resp?.oos.points ?? []).forEach((p) => m.set(p.date, p.rolling_sharpe ?? null));
      return m;
    });
    return dates.map((d) => {
      const row: Record<string, string | number | null> = { date: d };
      equitySeries.forEach((s, i) => { row[s.run_id] = lookups[i].get(d) ?? null; });
      return row;
    });
  }, [equitySeries]);

  if (ids.length < 2) {
    return (
      <div>
        <Link to="/runs/history" className="backlink">← Run history</Link>
        <div className="card" style={{ color: "#fca5a5", marginTop: 12 }}>
          Pick 2-15 runs from the run history page to compare.
        </div>
      </div>
    );
  }

  // Whether the backend returned the PM-style aggregates. Older deploys
  // won't, so the new charts gracefully degrade rather than erroring.
  const hasPm = data?.runs.some((r) => !!r.per_ticker_pm);

  return (
    <div>
      <Link to="/runs/history" className="backlink">← Run history</Link>
      <h2 style={{ marginTop: 12 }}>Compare runs — promotion view</h2>
      <div className="card" style={{ fontSize: 12, color: "#94a3b8" }}>
        Comparing {ids.length} run{ids.length === 1 ? "" : "s"}
        {isLoading && <span style={{ marginLeft: 8 }}>loading…</span>}
        <span style={{ marginLeft: 8 }}>· Phase {phase}</span>
      </div>

      {/* Run legend — maps shape + dash + color → config name → run_id.
          Every chart below identifies runs by SHAPE first (the protan-CVD
          user can't reliably tell colors apart in isolation); this card
          is the cross-reference between the chart marker and the run.
          The config name (feature_config_label) is the human-readable
          name; the run_id stays underneath in small monospace for
          navigation. */}
      {data && (
        <div className="card" style={{ background: "#0b1220", border: "1px solid #1e293b" }}>
          <div style={{ color: "#cbd5e1", fontSize: 11, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>
            🗝️ Legend — shape · dash · color = config (run id)
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {data.runs.map((r, idx) => {
              const label = runLabel(r);
              const cfgLabel = (r.config?.feature_config_label as string | undefined)?.trim() ?? "";
              const hasLabel = !!cfgLabel;
              const stats = cfgLabel ? statsByConfig[cfgLabel] : undefined;
              // Show a rejection chip when this run's saved config has a
              // promote-time rejection history. "Flaky" = ≥3 rejection-only
              // (run × ticker) pairs at >50% rate — same gate as the
              // leaderboard's status chip. Otherwise a softer "N rejections"
              // info chip if there are any at all.
              const rejOnly = stats?.rejection_only_total ?? 0;
              const rejRate = stats?.rejection_only_rate ?? 0;
              const promos = stats?.promotions_total ?? 0;
              const trials = stats?.run_ticker_pairs_total ?? 0;
              const isFlaky = rejOnly >= 3 && rejRate > 0.5;
              const showSoftChip = rejOnly > 0 && !isFlaky;
              const chipTip = stats
                ? `Config "${cfgLabel}" lifetime: ${promos}/${trials} (run × ticker) attempts promoted, ` +
                  `${rejOnly} ended with all candidates failing the CV gate ` +
                  `(${(rejRate * 100).toFixed(0)}%). ` +
                  (isFlaky
                    ? "Don't promote — re-run with a tuned search space first."
                    : "Promote with caution; check the per-ticker breakdown.")
                : "";
              return (
                <div
                  key={r.run_id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 10px",
                    background: "#0f172a",
                    border: isFlaky ? "1px solid #92400e" : "1px solid #334155",
                    borderRadius: 4,
                    minWidth: 200,
                  }}
                >
                  <RunGlyph idx={idx} size={14} />
                  <DashSwatch idx={idx} width={28} height={3} />
                  <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.25, flex: 1 }}>
                    <Link
                      to={`/runs/${encodeURIComponent(r.run_id)}`}
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: hasLabel ? "#e0e7ff" : "#94a3b8",
                        fontStyle: hasLabel ? "normal" : "italic",
                        textDecoration: "none",
                      }}
                    >
                      {label}
                    </Link>
                    <code style={{ fontSize: 10, color: "#64748b" }}>{r.run_id}</code>
                    {(isFlaky || showSoftChip) && (
                      <span
                        title={chipTip}
                        style={{
                          marginTop: 4,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          background: "#1c1917",
                          border: `1px solid ${isFlaky ? "#c2410c" : "#475569"}`,
                          color: isFlaky ? "#fed7aa" : "#cbd5e1",
                          padding: "1px 6px",
                          borderRadius: 3,
                          fontSize: 10,
                          fontWeight: 600,
                          alignSelf: "flex-start",
                          whiteSpace: "nowrap",
                          cursor: "help",
                        }}
                      >
                        {isFlaky ? "⚠️ Flaky CV gate" : `${rejOnly} prior CV-gate fail${rejOnly === 1 ? "" : "s"}`}
                        <span style={{ color: "#64748b", fontWeight: 400 }}>
                          {trials > 0 ? `· ${promos}/${trials} promoted` : ""}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ color: "#64748b", fontSize: 11, marginTop: 8, fontStyle: "italic" }}>
            Each chart below tags each run with the same shape glyph shown here. Use the shape to identify the run in any chart — color is intentionally secondary.
          </div>
        </div>
      )}

      {/* Top-of-page mission statement. Frames every chart that follows. */}
      <div className="card" style={{ background: "#0c1e3d", border: "1px solid #1e40af", borderLeft: "4px solid #3b82f6" }}>
        <h3 style={{ marginTop: 0, color: "#bfdbfe" }}>How to use this page to promote a model</h3>
        <div style={{ fontSize: 13, lineHeight: 1.6, color: "#e2e8f0" }}>
          <p style={{ marginTop: 0 }}>
            A hedge-fund PM allocating capital obsesses over <strong>“will this still work next quarter?”</strong>,
            not “did it look great in backtest.” The charts below are ordered to answer that
            question — start at the top, scan, narrow down.
          </p>
          <ol style={{ marginBottom: 0, paddingLeft: 18 }}>
            <li><strong>Risk/return scatter</strong> — the efficient-frontier shortlist. Upper-left dots
              (high return, low drawdown) are promotion candidates. Use it to pick which run
              dominates.</li>
            <li><strong>Strategy report card (radar)</strong> — at a glance, which run is well-rounded vs
              specialized. A spiky polygon = the run is great on one axis and weak on others
              (riskier). A balanced one = stable across dimensions.</li>
            <li><strong>Calmar heatmap</strong> — per-ticker breakdown. Find tickers where the model
              actually shines, and tickers where every run struggles.</li>
            <li><strong>Alpha vs buy-and-hold</strong> — the “did the model add value?” gut check.
              Negative bars mean a buy-and-hold of the ticker would have beaten the model. A
              positive CAGR alone isn't enough if the ticker doubled.</li>
          </ol>
        </div>
      </div>

      {error && <div className="card" style={{ color: "#fca5a5" }}>Error: {String(error)}</div>}

      {data && (
        <>
          {!hasPm && (
            <div className="card" style={{ background: "#1c1917", borderLeft: "3px solid #ea580c", color: "#fed7aa", fontSize: 12 }}>
              The new promotion charts need a backend that returns{" "}
              <code style={{ marginLeft: 4, marginRight: 4 }}>per_ticker_pm</code>{" "}
              aggregates. Deploy the latest DecisionEngineStack to enable them.
            </div>
          )}

          {/* 1. Risk/return scatter — headline view. */}
          {hasPm && (
            <div className="card">
              <h3 style={{ marginTop: 0 }}>Risk / return scatter — one dot per trial</h3>
              <Explainer
                title="How to read this"
                lines={[
                  "Each dot = one PPO training trial. X-axis = maximum drawdown (worst peak-to-trough % loss during the test window — magnitude shown, so further right = scarier). Y-axis = CAGR (annualized return). Dot size = number of trades that trial executed.",
                  "Upper-left corner = HIGH return + LOW drawdown = the strategies a PM wants to promote. Lower-right corner = lots of pain for not much profit.",
                  "Compare RUNS by color, not individual dots. A run is promotable if its CLOUD of dots sits upper-left of the others. A few lucky outliers in any cloud don't count — TPE samples a huge configuration space, so some seeds will randomly hit jackpots.",
                  "Watch for vertical spread: a run whose dots stretch from -50% to +20% drawdown is highly seed-dependent. Tight clouds = more trustworthy backtest scores.",
                ]}
              />
              <div style={{ width: "100%", height: 380 }}>
                <ResponsiveContainer>
                  <ScatterChart margin={{ top: 8, right: 16, bottom: 36, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis
                      type="number" dataKey="x" name="|Max Drawdown| %"
                      tick={{ fontSize: 11, fill: "#94a3b8" }}
                      label={{ value: "|Max Drawdown| % (further right = bigger losses)", position: "insideBottom", offset: -12, fill: "#94a3b8", fontSize: 11 }}
                    />
                    <YAxis
                      type="number" dataKey="y" name="CAGR %"
                      tick={{ fontSize: 11, fill: "#94a3b8" }}
                      label={{ value: "CAGR %", angle: -90, position: "insideLeft", fill: "#94a3b8", fontSize: 11 }}
                    />
                    <ZAxis type="number" dataKey="z" range={[20, 200]} />
                    <ReferenceLine y={0} stroke="#475569" strokeDasharray="4 4" label={{ value: "breakeven", fill: "#64748b", fontSize: 10, position: "insideTopRight" }} />
                    <Tooltip
                      cursor={{ strokeDasharray: "3 3" }}
                      contentStyle={{ background: "#0b1220", border: "1px solid #334155", fontSize: 12, color: "#e2e8f0" }}
                      itemStyle={{ color: "#e2e8f0" }}
                      formatter={(value: number | string, name: string) => {
                        if (name === "|Max Drawdown| %") return [typeof value === "number" ? value.toFixed(2) + "%" : value, "|MDD|"];
                        if (name === "CAGR %") return [typeof value === "number" ? value.toFixed(2) + "%" : value, "CAGR"];
                        return [value, name];
                      }}
                      labelFormatter={() => ""}
                    />
                    <Legend verticalAlign="top" align="center" iconType="line" wrapperStyle={{ fontSize: 12, color: "#cbd5e1", paddingBottom: 12, lineHeight: 1.9 }} />
                    {scatterSeries.map((s) => (
                      <Scatter
                        key={s.run_id}
                        // Recharts uses `name` as the legend label. Show the
                        // human-readable config label here (the legend in
                        // the page header already maps it to the run_id).
                        name={s.label}
                        data={s.points}
                        // Per-run shape is the PRIMARY identifier. Color is
                        // redundant cue only — overlapping colored clouds
                        // are unreadable for the protan-CVD primary user.
                        shape={s.shape}
                        fill={s.color}
                        fillOpacity={0.55}
                        stroke={s.color}
                        strokeWidth={0.5}
                        isAnimationActive={false}
                      />
                    ))}
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
              <div style={{ color: "#64748b", fontSize: 11, marginTop: 4, fontStyle: "italic" }}>
                Dot size scales with trade count. Tiny dots = strategies that barely traded
                (often a sign the agent learned to do nothing); large dots = high-turnover strategies.
                {scatterCounts.truncated && (
                  <>
                    {" "}Showing a deterministic sample of {scatterCounts.sampled.toLocaleString()}{" "}
                    of {scatterCounts.total.toLocaleString()} trials — the cloud shape is preserved
                    but individual outliers may not be plotted.
                  </>
                )}
              </div>
            </div>
          )}

          {/* 2. Per-run radar — strategy report card. */}
          {hasPm && (
            <div className="card">
              <h3 style={{ marginTop: 0 }}>Strategy report card</h3>
              <Explainer
                title="How to read this"
                lines={[
                  "Each colored polygon = one run. Each axis = one dimension a PM scores on. Bigger polygon (more area) = a more well-rounded strategy.",
                  "Return = median CAGR. Risk control = inverse of median max drawdown (so OUTER = small losses, INNER = big losses). Win rate = % of closed trades that made money. Profit factor = $ won ÷ $ lost (independent of win rate — you can win 90% of trades and still lose money if the losses are huge). Alpha vs B&H = how much CAGR the model added vs simply holding the ticker.",
                  "IMPORTANT: axes are normalized to the COMPARED RUNS, not universal scale. A run plotted at 1.0 on Return is the BEST of these runs — not necessarily good in absolute terms. Hover an axis label for what 'good' looks like in dollars.",
                  "Look for: (a) which run encloses the largest area (best all-rounder), (b) which has spikes vs gentle curves (specialized vs balanced), (c) where every run collapses inward (a dimension nobody is winning — possibly a hard ticker set).",
                ]}
              />
              <div style={{ width: "100%", height: 400 }}>
                <ResponsiveContainer>
                  <RadarChart data={radarData} outerRadius="75%">
                    <PolarGrid stroke="#334155" />
                    <PolarAngleAxis
                      dataKey="axis"
                      tick={(props: { payload: { value: string }; x: number; y: number; textAnchor?: string }) => {
                        const ax = RADAR_AXES.find((a) => a.label === props.payload.value);
                        const anchor = (props.textAnchor ?? "middle") as "start" | "middle" | "end";
                        return (
                          <text
                            x={props.x} y={props.y}
                            textAnchor={anchor}
                            fill="#cbd5e1" fontSize={12}
                            style={{ cursor: "help" }}
                          >
                            <title>{ax?.tip ?? ""}</title>
                            {props.payload.value}
                          </text>
                        );
                      }}
                    />
                    <PolarRadiusAxis angle={90} domain={[0, 1]} tick={{ fontSize: 10, fill: "#64748b" }} stroke="#1e293b" />
                    {data.runs.map((r, idx) => {
                      const st = runStyle(idx);
                      return (
                        <Radar
                          key={r.run_id}
                          // Legend label = config name; the top legend card
                          // maps it back to run_id and shape glyph.
                          name={runLabel(r)}
                          dataKey={r.run_id}
                          stroke={st.color}
                          // Dash pattern is the PRIMARY differentiator for
                          // overlapping polygons; color is secondary.
                          strokeDasharray={st.dash}
                          fill={st.color}
                          // Very low fill so overlapping polygons don't mud
                          // each other into a uniform purple-brown blob.
                          fillOpacity={0.06}
                          strokeWidth={2}
                          // Per-run shape at each vertex — gives a third
                          // independent identifier on top of color + dash.
                          dot={RadarVertexDot(idx)}
                          isAnimationActive={false}
                        />
                      );
                    })}
                    <Legend verticalAlign="top" align="center" iconType="line" wrapperStyle={{ fontSize: 12, color: "#cbd5e1", paddingBottom: 12, lineHeight: 1.9 }} />
                    <Tooltip
                      contentStyle={{ background: "#0b1220", border: "1px solid #334155", fontSize: 12, color: "#e2e8f0" }}
                      formatter={(_value: number | string, name: string, ctx: { payload?: Record<string, unknown> }) => {
                        // Show the raw value (stashed during data build), not
                        // the normalized 0-1 plot value, since the raw value
                        // is the interpretable one.
                        const raw = ctx.payload?.[`${name}__raw`];
                        return [String(raw ?? "—"), name];
                      }}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <div style={{ color: "#64748b", fontSize: 11, marginTop: 4, fontStyle: "italic" }}>
                Hover a data point to see the raw (unnormalized) value. Hover an axis label
                for what counts as a "good" number in absolute terms.
              </div>
            </div>
          )}

          {/* 2b. IS-vs-OOS bars (overfitting check) — Phase 2 only. */}
          {hasAnyIsData && (
            <div className="card">
              <h3 style={{ marginTop: 0 }}>In-sample vs Out-of-sample CAGR</h3>
              <Explainer
                title="The single most important overfitting check"
                lines={[
                  "Two bars per run: median CAGR on the TRAINING data (the bars the model saw and learned from), vs median CAGR on the TEST data (the held-out 6-month window the model has NEVER seen). 'CAGR' = compound annual growth rate.",
                  "Decision rule from quant research: if OOS CAGR is less than ~50% of IS CAGR, the model has OVERFIT — it learned patterns specific to the training period that don't generalize. Don't promote it, no matter how good the test number looks.",
                  "A healthy model: OOS bar is close to IS bar (or even higher — possible with a small train sample). A red flag: tall IS bar, tiny OOS bar. That's the model memorizing the past, not learning.",
                  "Why it works: PPO with thousands of trials WILL find configurations that look amazing on the training data by pure chance. The held-out test is the only data the search couldn't peek at. The gap between IS and OOS is the size of the lie the backtest is telling you.",
                  "Only Phase 2 trials have IS metrics (Phase 1 skips the IS eval to save compute — it's a search phase, not a validation phase). Switch to Phase 2 if this chart is empty.",
                ]}
              />
              <div style={{ width: "100%", height: 320 }}>
                <ResponsiveContainer>
                  <BarChart data={isOosCagrBarData} margin={{ top: 8, right: 16, bottom: 32, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="run_id" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#94a3b8" }}
                      label={{ value: "Median CAGR %", angle: -90, position: "insideLeft", fill: "#94a3b8", fontSize: 11 }}
                    />
                    <ReferenceLine y={0} stroke="#94a3b8" />
                    <Tooltip
                      cursor={{ fill: "#1e293b" }}
                      contentStyle={{ background: "#0b1220", border: "1px solid #334155", fontSize: 12, color: "#e2e8f0" }}
                      formatter={(value: number | string) =>
                        typeof value === "number" ? `${value.toFixed(2)}%` : value
                      }
                    />
                    <Legend verticalAlign="top" align="center" iconType="line" wrapperStyle={{ fontSize: 12, color: "#cbd5e1", paddingBottom: 12, lineHeight: 1.9 }} />
                    {/* Pair = ORANGE (in-sample/training) + BLUE (out-of-sample/test).
                        Blue↔orange is the only hue pair that stays clearly distinct
                        under protan CVD; the previous violet+emerald combo collapsed
                        to "blue+pale" for the primary user. Diagonal-hatch pattern
                        on the in-sample bar makes the pair distinguishable even at
                        zero color perception. */}
                    <defs>
                      <pattern id="is-bar-pattern" patternUnits="userSpaceOnUse" width={8} height={8}>
                        <rect width={8} height={8} fill="#E69F00" fillOpacity={0.4} />
                        <path d="M0,8 L8,0" stroke="#E69F00" strokeWidth={2} />
                      </pattern>
                    </defs>
                    <Bar dataKey="In-sample (training)" fill="url(#is-bar-pattern)" stroke="#E69F00" strokeWidth={1} isAnimationActive={false} />
                    <Bar dataKey="Out-of-sample (test)" fill="#56B4E9" isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div style={{ color: "#64748b", fontSize: 11, marginTop: 4, fontStyle: "italic" }}>
                When IS ≫ OOS, the model has overfit. When OOS ≥ IS × 0.5, it's likely learning something real.
              </div>
            </div>
          )}

          {/* 2c. Trial drilldown — per-trial equity views. */}
          {hasPm && tickers.length > 0 && (
            <div className="card">
              <h3 style={{ marginTop: 0 }}>Trial drilldown — best trial per run</h3>
              <Explainer
                title="What you're seeing"
                lines={[
                  "For the selected ticker, this fetches the BEST trial from each run (highest agent_mean_ret_pct, preferring Phase 2 if available) and overlays their equity trajectories. Three views answer three questions a PM asks:",
                  "📉 Underwater plot: 'How much pain would I have felt holding this?' Each colored shaded area below zero is one run's drawdown over time. Shallow + brief shading = comfortable to hold. Deep + sustained = a strategy that put you underwater for months — psychologically (and operationally) very hard to keep funded.",
                  "📈 Rolling 20-day Sharpe: 'Is the edge stable, or did one lucky month carry the score?' A line that hugs a positive band = consistent edge. A line that spikes high once then collapses = a one-trick strategy. Sharpe > 1 = decent risk-adjusted edge; > 2 = excellent; < 0 = the strategy was net-losing in that window.",
                  "🔗 Correlation matrix: 'If I ensemble these together, am I getting diversification, or just three copies of the same bet?' Cells show Pearson correlation of daily OOS returns. Closer to 0 (green) = independent bets, ideal for ensembling. Closer to 1 (red) = the runs make the same moves on the same days, so the ensemble adds no robustness.",
                ]}
              />
              {!drilldownEnabled ? (
                <div style={{ marginTop: 8 }}>
                  <button
                    onClick={() => setDrilldownEnabled(true)}
                    style={{
                      background: "#1e3a8a", color: "#bfdbfe",
                      border: "1px solid #2563eb", borderRadius: 4,
                      padding: "8px 14px", fontSize: 13, fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Load drilldown ({ids.length} run{ids.length === 1 ? "" : "s"} × 1 equity curve)
                  </button>
                  <div style={{ color: "#64748b", fontSize: 11, marginTop: 6, fontStyle: "italic" }}>
                    Skipped by default — the underwater / Sharpe / correlation views mount {ids.length} chart{ids.length === 1 ? "" : "s"} and fire {ids.length} fetch{ids.length === 1 ? "" : "es"}, which is the slowest part of this page on big compares.
                  </div>
                </div>
              ) : (<>
              <div style={{ marginTop: 4, marginBottom: 12 }}>
                <label style={{ color: "#94a3b8", fontSize: 12 }}>
                  Ticker:{" "}
                  <select
                    value={drilldownTicker}
                    onChange={(e) => setDrilldownTicker(e.target.value)}
                    style={{ background: "#0b1220", color: "#cbd5e1", border: "1px solid #334155", padding: "4px 8px", borderRadius: 3 }}
                  >
                    {tickers.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
              </div>

              {/* Loading / error roll-up. */}
              {equityQueries.some((q) => q.isLoading) && (
                <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 8 }}>
                  Loading equity curves…
                </div>
              )}
              {equitySeries.some((s) => s.error) && (
                <div style={{ color: "#fed7aa", fontSize: 12, marginBottom: 8 }}>
                  Some equity artifacts unavailable (trials run before the equity-curve
                  persistence deploy don't have this data). Re-run those trials to populate.
                </div>
              )}

              {/* Underwater (drawdown) plot — one mini-chart per run, stacked. */}
              <h4 style={{ marginTop: 16, color: "#cbd5e1" }}>Underwater plot — drawdown over time</h4>
              {equitySeries.map((s, sIdx) => {
                const pts = s.resp?.oos.points ?? [];
                if (s.loading) {
                  return <div key={s.run_id} style={{ color: "#94a3b8", fontSize: 12, marginBottom: 8 }}>Loading <code>{s.run_id}</code>…</div>;
                }
                if (!pts.length) {
                  return (
                    <div key={s.run_id} style={{ color: "#64748b", fontSize: 12, marginBottom: 8, padding: "6px 10px", background: "#0b1220", borderRadius: 4, borderLeft: `3px solid ${s.color}`, display: "flex", alignItems: "center", gap: 6 }}>
                      <RunGlyph idx={sIdx} size={10} />
                      <span style={{ color: "#e2e8f0", fontWeight: 600 }}>{s.label}</span>
                      <code style={{ color: "#64748b", fontSize: 10 }}>{s.run_id}</code>
                      <span>: no equity artifact available for {drilldownTicker}.</span>
                    </div>
                  );
                }
                return (
                  <div key={s.run_id} style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                      <RunGlyph idx={sIdx} size={11} />
                      <span style={{ color: s.color }}>{s.label}</span>
                      <code style={{ color: "#64748b", fontSize: 10, fontWeight: 400 }}>{s.run_id}</code>
                      {s.resp?.selected_trial && (
                        <span style={{ color: "#94a3b8", fontWeight: 400, marginLeft: 6 }}>
                          (trial #{s.resp.selected_trial.trial_number ?? "?"}, phase {s.resp.selected_trial.phase ?? "?"})
                        </span>
                      )}
                    </div>
                    <div style={{ width: "100%", height: 130 }}>
                      <ResponsiveContainer>
                        <AreaChart data={pts} margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                          <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} hide />
                          <YAxis
                            tick={{ fontSize: 10, fill: "#94a3b8" }}
                            label={{ value: "DD %", angle: -90, position: "insideLeft", fill: "#94a3b8", fontSize: 10 }}
                          />
                          <ReferenceLine y={0} stroke="#475569" />
                          <Tooltip
                            contentStyle={{ background: "#0b1220", border: "1px solid #334155", fontSize: 12, color: "#e2e8f0" }}
                            formatter={(v: number | string) => typeof v === "number" ? `${v.toFixed(2)}%` : v}
                            labelFormatter={(l: string) => l}
                          />
                          <Area type="monotone" dataKey="drawdown_pct" stroke={s.color} fill={s.color} fillOpacity={0.45} isAnimationActive={false} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                );
              })}

              {/* Rolling Sharpe — overlay all runs on one chart. */}
              <h4 style={{ marginTop: 24, color: "#cbd5e1" }}>Rolling 20-day Sharpe</h4>
              {sharpeChartData === null ? (
                <div style={{ color: "#64748b", fontSize: 12 }}>No equity data available for {drilldownTicker}.</div>
              ) : (
                <div style={{ width: "100%", height: 240 }}>
                  <ResponsiveContainer>
                    <LineChart data={sharpeChartData} margin={{ top: 4, right: 16, bottom: 28, left: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                      <YAxis
                        tick={{ fontSize: 11, fill: "#94a3b8" }}
                        label={{ value: "Sharpe (ann.)", angle: -90, position: "insideLeft", fill: "#94a3b8", fontSize: 11 }}
                      />
                      <ReferenceLine y={0} stroke="#475569" strokeDasharray="4 4" label={{ value: "0 = no edge", fill: "#64748b", fontSize: 10, position: "insideTopRight" }} />
                      <ReferenceLine y={1} stroke="#475569" strokeDasharray="2 4" label={{ value: "1 = decent", fill: "#64748b", fontSize: 10, position: "insideTopRight" }} />
                      <ReferenceLine y={2} stroke="#475569" strokeDasharray="2 4" label={{ value: "2 = excellent", fill: "#64748b", fontSize: 10, position: "insideTopRight" }} />
                      <Tooltip
                        contentStyle={{ background: "#0b1220", border: "1px solid #334155", fontSize: 12, color: "#e2e8f0" }}
                        formatter={(v: number | string) => typeof v === "number" ? v.toFixed(2) : v}
                      />
                      <Legend verticalAlign="top" align="center" iconType="line" wrapperStyle={{ fontSize: 12, color: "#cbd5e1", paddingBottom: 12, lineHeight: 1.9 }} />
                      {equitySeries.map((s) => {
                        const dataLen = sharpeChartData?.length ?? 0;
                        return (
                          <Line
                            key={s.run_id}
                            type="monotone"
                            // Legend label uses the config name; the user
                            // can map shape/dash back to a run via the
                            // top-of-page legend card.
                            name={s.label}
                            dataKey={s.run_id}
                            stroke={s.color}
                            // Per-run dash pattern is the PRIMARY
                            // identifier when colors overlap. Pair with
                            // the end-of-line label below.
                            strokeDasharray={s.dash}
                            dot={false}
                            strokeWidth={2}
                            connectNulls
                            isAnimationActive={false}
                          >
                            {/* Direct end-of-line label — the user
                                shouldn't have to bounce between line and
                                color legend. Only renders for the LAST
                                non-null point on each series. */}
                            <LabelList
                              dataKey={s.run_id}
                              content={(p) => {
                                const props = p as { x?: number | string; y?: number | string; index?: number; value?: number | string | null };
                                if (props.index !== dataLen - 1) return null;
                                if (props.value === null || props.value === undefined) return null;
                                const x = Number(props.x ?? 0) + 4;
                                const y = Number(props.y ?? 0) + 4;
                                return (
                                  <text x={x} y={y} fill={s.color} fontSize={10} fontWeight={700}>
                                    {s.label}
                                  </text>
                                );
                              }}
                            />
                          </Line>
                        );
                      })}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Correlation matrix — N×N table. */}
              <h4 style={{ marginTop: 24, color: "#cbd5e1" }}>Inter-run correlation (daily OOS returns)</h4>
              {equitySeries.every((s) => !s.resp?.oos.points.length) ? (
                <div style={{ color: "#64748b", fontSize: 12 }}>
                  Correlation needs equity curves from at least two runs on {drilldownTicker}.
                </div>
              ) : (
                <div className="table-wrap" style={{ maxWidth: 720 }}>
                  <table>
                    <thead>
                      <tr>
                        <th></th>
                        {ids.map((rid, j) => (
                          <th key={rid} className="num">
                            <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }}>
                              <RunGlyph idx={j} size={10} />
                              <span style={{ fontSize: 11, color: "#e2e8f0", fontWeight: 700 }}>{labelByRunId[rid] || rid}</span>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {ids.map((rid_i, i) => (
                        <tr key={rid_i}>
                          <td style={{ fontWeight: 600 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                              <RunGlyph idx={i} size={10} />
                              <span style={{ fontSize: 11, color: "#e2e8f0" }}>{labelByRunId[rid_i] || rid_i}</span>
                            </div>
                          </td>
                          {ids.map((rid_j, j) => {
                            const r = corrMatrix[i]?.[j];
                            if (r === undefined || Number.isNaN(r)) {
                              return <td key={rid_j} className="num" style={{ color: "#475569" }}>—</td>;
                            }
                            return <CorrCell key={rid_j} r={r} />;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div style={{ color: "#64748b", fontSize: 11, marginTop: 8, fontStyle: "italic" }}>
                Green ≈ 0 (independent — great for ensembling). Red ≈ +1 (the runs make the
                same bets on the same days — redundant in an ensemble). Amber = negative
                correlation (rare; the runs actively hedge each other).
              </div>
              </>)}
            </div>
          )}

          {/* 3. Calmar heatmap. */}
          <div className="card">
            <h3 style={{ marginTop: 0 }}>Calmar heatmap — runs × tickers</h3>
            <Explainer
              title="How to read this"
              lines={[
                "Cells show median agent_mean_ret_pct for that (run, ticker) — a Calmar-style score = CAGR ÷ max(|max drawdown|, 1%). Higher = better return per unit of drawdown pain.",
                "Each cell shows: the number, a horizontal bar (length = magnitude, BLUE right = positive, ORANGE left = negative, centered tick = zero), and a rank badge (★1/N = best run for that ticker). The bar lets you compare cells without needing to read shades; the rank tells you the row winner in plain text.",
                "Scan rows: a ticker where one run's bar dominates is a candidate for promotion FOR THAT TICKER. Scan columns: a run with consistently long blue bars across many tickers is a broadly capable model.",
                "What's the magic number? >1.0 = a model that returns its peak drawdown back in one year. >2.0 starts to look like a real edge. Below 0 means losses dominated — don't promote.",
              ]}
            />
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Ticker</th>
                    {data.runs.map((r, idx) => (
                      <th key={r.run_id} className="num" style={{ minWidth: 110 }}>
                        <Link
                          to={`/runs/${encodeURIComponent(r.run_id)}`}
                          style={{ color: "#e2e8f0", textDecoration: "none" }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                            <RunGlyph idx={idx} size={11} />
                            <span style={{ fontWeight: 700 }}>{runLabel(r)}</span>
                          </div>
                          <div style={{ fontSize: 9, color: "#64748b", fontFamily: "ui-monospace, monospace", fontWeight: 400, textAlign: "right" }}>
                            {r.run_id}
                          </div>
                        </Link>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tickers.map((t) => {
                    const cells = data.runs.map((r) => pickStat(r.per_ticker, t, "median"));
                    // Per-row rank — 1 = highest. Ties get the same rank.
                    // Cells with no value (null) are excluded from ranking.
                    const ranked = cells
                      .map((v, i) => ({ v, i }))
                      .filter((x) => typeof x.v === "number")
                      .sort((a, b) => (b.v as number) - (a.v as number));
                    const rankByIdx: Record<number, number> = {};
                    ranked.forEach((entry, ord) => { rankByIdx[entry.i] = ord + 1; });
                    const rowRunCount = ranked.length;
                    return (
                      <tr key={t}>
                        <td><code style={{ fontSize: 12 }}>{t}</code></td>
                        {cells.map((m, i) => (
                          <HeatCell
                            key={i}
                            value={typeof m === "number" ? m : null}
                            gMin={heatRange.min}
                            gMax={heatRange.max}
                            rank={rankByIdx[i] ?? null}
                            runCount={rowRunCount}
                          />
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* 4. Alpha vs buy-and-hold. */}
          {hasPm && (
            <div className="card">
              <h3 style={{ marginTop: 0 }}>Alpha vs buy-and-hold</h3>
              <Explainer
                title="How to read this"
                lines={[
                  "For each ticker, this is (model's median CAGR) − (median buy-and-hold CAGR over the same test window). Positive = the PPO model added value above just owning the stock. Negative = a passive buy-and-hold would have beaten the model.",
                  "This is the single most damning chart for a 'great-looking' model. A model with +30% CAGR on NVDA looks amazing UNTIL you remember NVDA was up 40% over the same window — in which case the model COST you 10% vs doing nothing.",
                  "Decision rule: a model that's negative on more than half of its tickers usually isn't earning its keep. The bar should be solidly green across most tickers, OR the green bars should be on the tickers you actually plan to trade.",
                  "Tiny bars near zero on volatile tickers are also suspect — the model may just be tracking the ticker passively without truly adding edge.",
                ]}
              />
              <div style={{ width: "100%", height: 360 }}>
                <ResponsiveContainer>
                  <BarChart data={alphaBarData} margin={{ top: 8, right: 16, bottom: 36, left: 8 }}>
                    {/* Pattern fills (diagonal / cross-hatch / dots / vertical-stripe)
                        per run — adjacent bars are distinguishable by FILL
                        PATTERN, not just hue. Hard-required because the
                        protan-CVD primary user can't reliably tell two
                        same-luminance hues apart when they're stacked side-by-side. */}
                    <RunFillPatternDefs runCount={data.runs.length} />
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis
                      dataKey="ticker"
                      tick={{ fontSize: 11, fill: "#94a3b8" }}
                      label={{ value: "Ticker", position: "insideBottom", offset: -12, fill: "#94a3b8", fontSize: 11 }}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#94a3b8" }}
                      label={{ value: "Alpha (CAGR − B&H) %", angle: -90, position: "insideLeft", fill: "#94a3b8", fontSize: 11 }}
                    />
                    <ReferenceLine y={0} stroke="#94a3b8" />
                    <Tooltip
                      cursor={{ fill: "#1e293b" }}
                      contentStyle={{ background: "#0b1220", border: "1px solid #334155", fontSize: 12, color: "#e2e8f0" }}
                      formatter={(value: number | string) =>
                        typeof value === "number" ? `${value.toFixed(2)}%` : value
                      }
                    />
                    <Legend verticalAlign="top" align="center" iconType="line" wrapperStyle={{ fontSize: 12, color: "#cbd5e1", paddingBottom: 12, lineHeight: 1.9 }} />
                    {data.runs.map((r, idx) => (
                      <Bar
                        key={r.run_id}
                        // Legend label = config name (top legend card maps
                        // it to run_id + shape glyph).
                        name={runLabel(r)}
                        dataKey={r.run_id}
                        // Pattern-fill takes priority over color as the
                        // bar's per-run signature. RunFillPatternDefs above
                        // emits one <pattern> per run index.
                        fill={`url(#run-fill-${idx})`}
                        stroke={runStyle(idx).color}
                        strokeWidth={1}
                        isAnimationActive={false}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div style={{ color: "#64748b", fontSize: 11, marginTop: 6, fontStyle: "italic" }}>
                Bars are distinguished by FILL PATTERN as well as color. Cross-reference the pattern with the top legend card to find which run a bar belongs to without having to read color shades.
              </div>
            </div>
          )}

          {/* ---------------- Legacy detail (kept) ---------------- */}

          {/* Phase 1 training cost — pick the cheapest comparable config. */}
          {data.runs.some((r) => r.phase1_cost) && (() => {
            // Green = cheapest in the comparison, red = priciest. We color
            // by mean-per-trial because that's the most directly comparable
            // signal (total compute also tracks trial COUNT, which can
            // differ across runs when one was stopped early).
            const meanVals = data.runs
              .map((r) => r.phase1_cost?.mean_seconds)
              .filter((v): v is number => typeof v === "number" && v > 0);
            const meanLo = meanVals.length ? minOf(meanVals) : 0;
            const meanHi = meanVals.length ? maxOf(meanVals) : 0;
            const costColor = (v: number | undefined): string => {
              if (typeof v !== "number" || meanLo === meanHi) return "#e2e8f0";
              const norm = (v - meanLo) / (meanHi - meanLo);
              if (norm < 0.34) return "#22c55e";  // cheapest third
              if (norm > 0.66) return "#ef4444";  // priciest third
              return "#fbbf24";
            };
            return (
              <div className="card">
                <h3 style={{ marginTop: 0 }}>Phase 1 training cost</h3>
                <Explainer
                  title="Pick the cheapest comparable config"
                  lines={[
                    "When two configs score similarly on the quality charts above, the one that trains faster per trial is the better bet — both for Phase 2 (replicates re-train at the same per-trial speed) and for production retrains.",
                    "Total compute = sum of every Phase 1 trial's wall-clock training time. It's the right cost proxy because trials run on Spot ECS at $/instance-hour — total compute × spot rate ≈ $ spent on this run's search.",
                    "Mean / trial is the most directly comparable number: a config that trains one trial in 24s vs another at 36s will be ~1.5× cheaper across Phase 2 and every future retrain. Mean per trial × ~350 = expected Phase 2 wall-clock per ticker promoted (350 = top-K configs × replicates).",
                    "Green = cheapest of the compared runs (by mean / trial); red = priciest. Use this AFTER picking promotable candidates from the quality charts — never promote a fast-but-bad config.",
                  ]}
                />
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Run</th>
                        <th className="num">Phase 1 trials</th>
                        <th className="num">Total compute</th>
                        <th className="num">Mean / trial</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.runs.map((r, idx) => {
                        const c = r.phase1_cost;
                        return (
                          <tr key={r.run_id}>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <RunGlyph idx={idx} size={11} />
                                <span style={{ fontWeight: 700, color: "#e2e8f0" }}>{runLabel(r)}</span>
                                <code style={{ fontSize: 10, color: "#64748b", fontWeight: 400 }}>{r.run_id}</code>
                              </div>
                            </td>
                            <td className="num">{c ? c.trial_count.toLocaleString() : "—"}</td>
                            <td className="num">{c ? fmtDuration(c.total_seconds) : "—"}</td>
                            <td
                              className="num"
                              style={{ color: costColor(c?.mean_seconds), fontWeight: 600 }}
                            >
                              {c ? fmtDuration(c.mean_seconds) : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {/* Config profile labels */}
          <div className="card">
            <h3 style={{ marginTop: 0 }}>Config profile</h3>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {data.runs.map((r, idx) => {
                const cfg = r.config as Record<string, unknown> | undefined;
                const label = cfg?.feature_config_label as string | undefined;
                const overrides = cfg?.feature_overrides as Record<string, unknown> | undefined;
                const pinCount = overrides ? Object.keys(overrides).length : 0;
                return (
                  <div
                    key={r.run_id}
                    style={{
                      flex: "1 1 220px",
                      background: label ? "#1e1b4b" : "#1e293b",
                      border: `1px solid ${label ? "#4c1d95" : "#334155"}`,
                      borderLeft: `4px solid ${colorFor(idx)}`,
                      borderRadius: 6, padding: "10px 12px",
                    }}
                  >
                    <div style={{ color: "#94a3b8", fontSize: 11, marginBottom: 4 }}>
                      <code>{r.run_id}</code>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: label ? "#c4b5fd" : "#94a3b8" }}>
                      {label ? <code style={{ color: "#e0e7ff" }}>{label}</code>
                             : <em>default search space</em>}
                    </div>
                    <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 2 }}>
                      {pinCount > 0 ? `${pinCount} pinned override${pinCount === 1 ? "" : "s"}`
                                    : "no pins — Optuna searched full space"}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Config diff */}
          <div className="card">
            <h3 style={{ marginTop: 0 }}>Config differences</h3>
            {data.config_diff.length === 0 ? (
              <div style={{ color: "#94a3b8", fontStyle: "italic" }}>
                All input config fields are identical across the selected runs. The ablation surface
                is whatever changed in code between runs (not visible here).
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Field</th>
                      {data.runs.map((r, idx) => (
                        <th key={r.run_id}>
                          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <RunGlyph idx={idx} size={10} />
                            <span style={{ fontSize: 11, color: "#e2e8f0", fontWeight: 700 }}>{runLabel(r)}</span>
                          </div>
                          <code style={{ fontSize: 9, color: "#64748b" }}>{r.run_id}</code>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.config_diff.map((d) => (
                      <tr key={d.field}>
                        <td style={{ fontWeight: 600 }}>{d.field}</td>
                        {d.values.map((v, i) => (
                          <td key={i} style={{ fontSize: 12, fontFamily: "ui-monospace, monospace" }}>{fmtVal(v)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Per-ticker median comparison (legacy quick table) */}
          <div className="card">
            <h3 style={{ marginTop: 0 }}>Median score by ticker (raw)</h3>
            <div style={{ color: "#64748b", fontSize: 11, marginBottom: 8 }}>
              Same data as the heatmap above, in numeric form. Green = highest median across
              runs for that ticker, red = lowest.{" "}
              <strong style={{ color: "#cbd5e1" }}>Click a number</strong> to promote that
              run's best Phase 2 trial for the ticker to paper-live (pinned).
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Ticker</th>
                    {data.runs.map((r, idx) => (
                      <th key={r.run_id} className="num">
                        <Link to={`/runs/${encodeURIComponent(r.run_id)}`} style={{ color: "#e2e8f0", textDecoration: "none" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }}>
                            <RunGlyph idx={idx} size={10} />
                            <span style={{ fontWeight: 700 }}>{runLabel(r)}</span>
                          </div>
                          <code style={{ fontSize: 9, color: "#64748b", fontFamily: "ui-monospace, monospace" }}>{r.run_id}</code>
                        </Link>
                      </th>
                    ))}
                    <th
                      className="num"
                      title="Median agent_mean_ret_pct across the currently-deployed paper-live ensemble for each ticker. This is the metric of the model actually trading right now."
                      style={{ color: "#86efac", borderLeft: "2px solid #14532d" }}
                    >
                      Live (deployed)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tickers.map((t) => {
                    const medians = data.runs.map((r) => pickStat(r.per_ticker, t, "median"));
                    const present = medians.filter((m): m is number => typeof m === "number");
                    const lo = present.length ? minOf(present) : 0;
                    const hi = present.length ? maxOf(present) : 0;
                    const live = data.paper_live_per_ticker?.[t];
                    return (
                      <tr key={t}>
                        <td><code style={{ fontSize: 12 }}>{t}</code></td>
                        {medians.map((m, i) => {
                          const runId = data.runs[i].run_id;
                          if (typeof m !== "number") {
                            return <td key={i} className="num"><span style={{ color: "#64748b" }}>—</span></td>;
                          }
                          return (
                            <PromoteMedianCell
                              key={i}
                              runId={runId}
                              ticker={t}
                              median={m}
                              lo={lo}
                              hi={hi}
                              status={promoteCells[`${runId}|${t}`]}
                              onPromote={handlePromoteCell}
                            />
                          );
                        })}
                        {/* Currently-deployed paper-live ensemble median.
                            Green tint so it's unambiguous which column
                            represents the model actually trading. */}
                        <td
                          className="num"
                          title={live
                            ? `Paper-live ensemble median: ${live.median.toFixed(3)} ` +
                              `(n=${live.count}, range ${live.min.toFixed(3)}–${live.max.toFixed(3)})\n` +
                              `From run${live.run_ids.length === 1 ? "" : "s"}: ${live.run_ids.join(", ")}`
                            : "No paper-live model deployed for this ticker"}
                          style={{
                            background: live ? "rgba(34, 197, 94, 0.12)" : "transparent",
                            borderLeft: "2px solid #14532d",
                            color: live ? "#86efac" : "#475569",
                            fontWeight: 600,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {live ? live.median.toFixed(3) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Per-run distribution detail (legacy) */}
          {data.runs.map((r: RunCompareEntry, idx) => (
            <div className="card" key={r.run_id} style={{ borderLeft: `4px solid ${colorFor(idx)}` }}>
              <h3 style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <RunGlyph idx={idx} size={14} />
                <span style={{ color: "#e2e8f0" }}>{runLabel(r)}</span>
                <code style={{ color: "#64748b", fontSize: 12, fontWeight: 400 }}>{r.run_id}</code>
                <span style={{ color: "#94a3b8", fontSize: 13, fontWeight: 400, marginLeft: 8 }}>
                  {r.trial_count} trials
                </span>
                {" "}
                <Link to={`/runs/${encodeURIComponent(r.run_id)}`} style={{ color: "#93c5fd", fontSize: 13, fontWeight: 400 }}>
                  inspect →
                </Link>
              </h3>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Ticker</th>
                      <th className="num">N</th>
                      <th className="num">min</th>
                      <th className="num">p25</th>
                      <th className="num">median</th>
                      <th className="num">p75</th>
                      <th className="num">p90</th>
                      <th className="num">max</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(r.per_ticker).sort(([a], [b]) => a.localeCompare(b)).map(([t, s]) => (
                      <tr key={t}>
                        <td><code style={{ fontSize: 12 }}>{t}</code></td>
                        <td className="num">{s.count}</td>
                        <td className="num">{fmtScore(s.min)}</td>
                        <td className="num">{fmtScore(s.p25)}</td>
                        <td className="num" style={{ fontWeight: 600 }}>{fmtScore(s.median)}</td>
                        <td className="num">{fmtScore(s.p75)}</td>
                        <td className="num">{fmtScore(s.p90)}</td>
                        <td className="num" style={{ color: "#22c55e" }}>{fmtScore(s.max)}</td>
                      </tr>
                    ))}
                    {Object.keys(r.per_ticker).length === 0 && (
                      <tr><td colSpan={8} style={{ textAlign: "center", color: "#94a3b8", fontStyle: "italic", padding: 12 }}>
                        No phase-{phase} trials found for this run.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </>
      )}

      {promoteTarget !== null && (
        <PromoteConfirmModal
          target={promoteTarget}
          configLabel={configLabelForRun(data, promoteTarget.runId)}
          stats={statsByConfig[configLabelForRun(data, promoteTarget.runId)]}
          onCancel={() => setPromoteTarget(null)}
          onConfirm={runPromote}
        />
      )}
      {noPhase2Target !== null && (
        <StartTargetedPhase2Modal
          target={noPhase2Target}
          configLabel={configLabelForRun(data, noPhase2Target.runId)}
          stats={statsByConfig[configLabelForRun(data, noPhase2Target.runId)]}
          onCancel={() => setNoPhase2Target(null)}
          onConfirm={runStartTargetedPhase2}
        />
      )}
    </div>
  );
}

/** Resolve a run's saved-config name (the human label, e.g. "intraday-scalper")
 *  from the loaded run-compare payload. Returns "" if the run isn't in the
 *  payload or doesn't have a config label (inline / default-search-space). */
function configLabelForRun(
  data: { runs: RunCompareEntry[] } | undefined,
  runId: string,
): string {
  if (!data) return "";
  const run = data.runs.find((r) => r.run_id === runId);
  return (run?.config?.feature_config_label as string | undefined)?.trim() ?? "";
}

/** Warning block rendered in the Start-Phase-2 / Promote modals when the
 *  selected run's config has a meaningful rejection history. Three tiers:
 *  high (>50% all-fail rate, ≥3 attempts) → red panel, "Don't run"; medium
 *  (>20%, ≥2) → amber panel, "Look closer"; low / none → no panel. */
function ConfigRejectionWarning({
  configLabel,
  stats,
  action,
}: {
  configLabel: string;
  stats?: ConfigPromotionStats;
  /** Verb shown in the warning: "promote" or "start Phase 2 for". */
  action: string;
}) {
  if (!configLabel || !stats) return null;
  const rejOnly = stats.rejection_only_total;
  const rejRate = stats.rejection_only_rate;
  const promos = stats.promotions_total;
  const trials = stats.run_ticker_pairs_total;
  if (rejOnly < 2 || rejRate <= 0.2) return null;
  const isHigh = rejOnly >= 3 && rejRate > 0.5;
  const pct = Math.round(rejRate * 100);
  return (
    <div
      role="alert"
      style={{
        marginBottom: 14,
        padding: "10px 12px",
        background: isHigh ? "#3f1d1d" : "#1c1917",
        border: `1px solid ${isHigh ? "#dc2626" : "#c2410c"}`,
        borderLeft: `4px solid ${isHigh ? "#ef4444" : "#f59e0b"}`,
        borderRadius: 4,
        color: isHigh ? "#fecaca" : "#fed7aa",
        fontSize: 13,
        lineHeight: 1.55,
      }}
    >
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>
        ⚠️ You are about to {action} a configuration that has failed promotion {pct}% of the time
      </div>
      <div style={{ fontSize: 12, color: isHigh ? "#fecaca" : "#fed7aa" }}>
        Config <code style={{ color: "#fff", background: "rgba(0,0,0,0.25)", padding: "0 4px", borderRadius: 2 }}>{configLabel}</code>:{" "}
        <strong>{rejOnly} of {trials}</strong> prior (run × ticker) attempts ended with{" "}
        <strong>every Phase 2 candidate failing</strong> the consistency gate (no model promoted).{" "}
        Only <strong>{promos}/{trials}</strong> attempts produced a tradeable model.
      </div>
      {isHigh ? (
        <div style={{ fontSize: 12, marginTop: 8, color: "#fecaca" }}>
          <strong>Recommended:</strong> retire or tune this config before spending more compute.
          The new run is likely to produce another unpromotable result.
        </div>
      ) : (
        <div style={{ fontSize: 12, marginTop: 8, color: "#fed7aa" }}>
          <strong>Proceed with caution.</strong> Watch the next run's CV gate result on the
          leaderboard.
        </div>
      )}
    </div>
  );
}

function PromoteConfirmModal({
  target,
  configLabel,
  stats,
  onCancel,
  onConfirm,
}: {
  target: { runId: string; ticker: string; median: number };
  configLabel: string;
  stats?: ConfigPromotionStats;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);
  return (
    <div
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-label="Confirm promotion"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
        zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#111827", border: "1px solid #1e293b", borderRadius: 8,
          width: "100%", maxWidth: 480, padding: 20,
        }}
      >
        <h3 style={{ margin: 0, color: "#e2e8f0", marginBottom: 12 }}>
          Promote to paper-live?
        </h3>
        <ConfigRejectionWarning configLabel={configLabel} stats={stats} action="promote with" />
        <div style={{ color: "#cbd5e1", fontSize: 13, lineHeight: 1.55, marginBottom: 8 }}>
          Promote the best Phase 2 trial for <code style={{ color: "#e0e7ff" }}>{target.ticker}</code>{" "}
          from run <code style={{ color: "#e0e7ff" }}>{target.runId}</code> to paper-live?
        </div>
        <div style={{ color: "#94a3b8", fontSize: 12, lineHeight: 1.55, marginBottom: 16 }}>
          Median score for {target.ticker} in this run: <strong style={{ color: "#cbd5e1" }}>{target.median.toFixed(3)}</strong>.
          The exact (config_key, replicate) is resolved server-side using the same
          aggregation as auto-promote (best config by mean replicate score, then best
          replicate within that config). The promoted row is pinned so the next
          auto-promote cycle won't overwrite it.
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            onClick={onCancel}
            style={{
              background: "#1e293b", border: "1px solid #334155",
              color: "#cbd5e1", padding: "8px 16px", borderRadius: 4,
              fontSize: 14, cursor: "pointer",
            }}
          >Cancel</button>
          <button
            onClick={onConfirm}
            style={{
              background: "#7c3aed", color: "#fff", border: "1px solid #5b21b6",
              padding: "8px 16px", borderRadius: 4,
              fontSize: 14, fontWeight: 600, cursor: "pointer",
            }}
          >Promote</button>
        </div>
      </div>
    </div>
  );
}

function StartTargetedPhase2Modal({
  target,
  configLabel,
  stats,
  onCancel,
  onConfirm,
}: {
  target: { runId: string; ticker: string };
  configLabel: string;
  stats?: ConfigPromotionStats;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);
  return (
    <div
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-label="Start targeted Phase 2"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
        zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#111827", border: "1px solid #1e293b", borderRadius: 8,
          width: "100%", maxWidth: 520, padding: 20,
        }}
      >
        <h3 style={{ margin: 0, color: "#fde68a", marginBottom: 12 }}>
          No Phase 2 yet — start one for {target.ticker}?
        </h3>
        <ConfigRejectionWarning configLabel={configLabel} stats={stats} action="start Phase 2 for" />
        <div style={{ color: "#cbd5e1", fontSize: 13, lineHeight: 1.55, marginBottom: 10 }}>
          This run doesn't have Phase 2 replicates for{" "}
          <code style={{ color: "#e0e7ff" }}>{target.ticker}</code> — so there's no
          validated trial to promote yet.
        </div>
        <div style={{ color: "#94a3b8", fontSize: 12, lineHeight: 1.55, marginBottom: 16 }}>
          Starting a <strong style={{ color: "#fde68a" }}>targeted Phase 2</strong>{" "}
          will take the top Phase 1 trials for <strong>{target.ticker}</strong> only and
          train replicates of each — roughly 350 jobs on the shared ASG (~30 min).
          The other tickers in run <code style={{ color: "#cbd5e1" }}>{target.runId}</code>{" "}
          are <em>not</em> trained, so you don't pay for compute you'd throw away.
          When it finishes, click the median cell again to promote.
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            onClick={onCancel}
            style={{
              background: "#1e293b", border: "1px solid #334155",
              color: "#cbd5e1", padding: "8px 16px", borderRadius: 4,
              fontSize: 14, cursor: "pointer",
            }}
          >Cancel</button>
          <button
            onClick={onConfirm}
            style={{
              background: "#ca8a04", color: "#0f172a",
              border: "1px solid #a16207",
              padding: "8px 16px", borderRadius: 4,
              fontSize: 14, fontWeight: 700, cursor: "pointer",
            }}
          >Start Phase 2 for {target.ticker}</button>
        </div>
      </div>
    </div>
  );
}

